import { useEffect, useState } from 'react';
import { Field, Image, withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';

import { icon_arrow_right } from '@gn-audio/icons';
import { Button } from '@gn-audio/jss-components';
import { JabraIcon } from '@gn-audio/jss-components';
import { Award } from '@gn-audio/jss-core';

import { getFamilyAwards } from 'api/marketingDocumentsApi';
import { JabraGallerySlider, JabraGallerySliderItem } from 'common/components/JabraGalerySlider';
import { getSharedDictionary } from 'common/getters/getSharedDictionary';
import { useProductField } from 'common/PageProductContext/pageProductContext';
import { ComponentProps } from 'lib/component-props';
import useFallbackTranslation from 'utils/hooks/useFallbackTranslation';

type AwardsSpotImage = {
  index: number;
  active: boolean;
  item: { identifier: string } & Award;
  onClick: () => void;
};

const cardShadow =
  'shadow-[0px_20px_25px_-5px_rgba(0,0,0,0.10),0px_10px_10px_0px_rgba(0,0,0,0.04)]';

const AwardsSpotImage = ({ item, index, active, onClick }: AwardsSpotImage) => {
  const { t } = useFallbackTranslation();
  return (
    <JabraGallerySliderItem
      index={index}
      onClick={onClick}
      active={active}
      className="hover group shrink-0 grow-0 basis-1/2 first:pl-6 sm:basis-[45%] md:basis-[calc(33.33%-24px)] lg:basis-[calc(20%-24px)] 2xl:basis-[calc(14.28%-24px)]"
    >
      <div
        className={classNames({
          [`group-hover:flex group-hover:h-[305px] group-hover:flex-col group-hover:items-center group-hover:gap-6 group-hover:rounded-xl group-hover:bg-[rgba(255,255,255,0.4)] group-hover:p-6 group-hover:${cardShadow}`]:
            !active,
          [`flex h-[305px] flex-col items-center gap-6 rounded-xl bg-white p-6 ${cardShadow}`]:
            active,
        })}
      >
        <div
          className={classNames(
            'flex min-h-[200px] items-center justify-center',
            !active && 'min-h-[305px] group-hover:min-h-[200px]'
          )}
        >
          <Image
            className={classNames('w-[126px] group-hover:w-[150px]', active && 'w-[150px]')}
            field={{ src: item.splashUrl }}
          />
        </div>
        <Button
          variant="tertiary"
          size="s"
          className={classNames((!active || !item.splashUrl) && 'hidden')}
          icon={<JabraIcon className="w-4" Icon={icon_arrow_right} />}
          href={item.websiteUrl}
          aria-label={t('system.LearnMore')}
          asLink
        />
      </div>
    </JabraGallerySliderItem>
  );
};

type AwardsSpotProps = {
  fields: {
    title: Field<string>;
    titleShared: Field<string>;
  };
  props: {
    marketingDocumentsBaseUrl: string;
  };
} & ComponentProps;

const AwardsSpot = ({ fields, props }: AwardsSpotProps) => {
  const { productId } = useProductField();
  const [familyAwards, setFamilyAwards] = useState<Award[]>([]);
  const { locale: getLocale } = useFallbackTranslation();
  const title = getSharedDictionary(fields, 'title')?.value;

  useEffect(() => {
    const getData = async () => {
      const familyId = productId;

      if (familyId) {
        const locale = getLocale();
        const awardsList =
          (await getFamilyAwards(props.marketingDocumentsBaseUrl, familyId, 'en')) || [];

        if (locale != 'en') {
          awardsList.push(
            ...((await getFamilyAwards(props.marketingDocumentsBaseUrl, familyId, locale)) || [])
          );
        }

        setFamilyAwards(awardsList);
      }
    };

    void getData();
  }, [productId, getLocale, props.marketingDocumentsBaseUrl]);

  if (!Boolean(familyAwards.length)) {
    return null;
  }

  const items = familyAwards.map((award) => ({
    identifier: award.splashUrl as string,
    ...award,
  }));

  return (
    <div className="bg-beige-50">
      <div className="max-md:container-fluid md:container">
        <h2 className="text-center text-[32px] font-bold leading-9 md:text-5xl md:leading-[66px]">
          {title}
        </h2>
        <JabraGallerySlider
          items={items}
          itemComponent={AwardsSpotImage}
          itemsAmountStep={2}
          containerClassName="pb-12 pt-14 gap-6"
        />
      </div>
    </div>
  );
};

export async function getStaticProps() {
  return {
    props: { marketingDocumentsBaseUrl: process.env.MARKETING_DOCUMENTS_BASE_URL },
  };
}

export default withDatasourceCheck()(AwardsSpot);
